import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'

const phoneUtil = PhoneNumberUtil.getInstance()

/**
 * Helper to parse a phone number and infer its region.
 * Returns { number, region } or null if invalid.
 */
function parseAndInferRegion(input: string) {
  if (typeof input !== 'string' || input.length === 0) {
    return null
  }
  try {
    // If the input starts with + or 00, treat as international, else default to US
    const isInternational = /^\s*(\+|00)/.test(input)
    const region = isInternational ? 'ZZ' : 'US'
    const number = phoneUtil.parse(input, region)
    const inferredRegion = phoneUtil.getRegionCodeForNumber(number)
    return { number, region: inferredRegion }
  } catch {
    return null
  }
}

/**
 * Formats a phone number in the national format for its inferred region.
 * E.g., (415) 555-2671 for US.
 */
export const phoneFormatFilter = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) {
    return ''
  }
  const parsed = parseAndInferRegion(input)
  if (!parsed) {
    return input
  }
  try {
    return phoneUtil.format(parsed.number, PhoneNumberFormat.NATIONAL)
  } catch {
    return input
  }
}

/**
 * Formats a phone number in the international format (with country code).
 * E.g., +1 415-555-2671 for US.
 */
export const phoneFormatFilterInternational = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) {
    return ''
  }
  const parsed = parseAndInferRegion(input)
  if (!parsed) {
    return input
  }
  try {
    return phoneUtil.format(parsed.number, PhoneNumberFormat.INTERNATIONAL)
  } catch {
    return input
  }
}

/**
 * Validates if a given phone number string is a valid phone number.
 *
 * @param input - The phone number as a string, potentially including non-numeric characters.
 * @returns True if the phone number is valid, false otherwise.
 */
export const isValidPhoneNumber = (input: string): boolean => {
  if (typeof input !== 'string' || input.length === 0) {
    return false
  }
  const parsed = parseAndInferRegion(input)
  if (!parsed) {
    return false
  }
  try {
    return phoneUtil.isValidNumberForRegion(parsed.number, parsed.region)
  } catch {
    return false
  }
}

/**
 * Normalizes a given phone number string to a standard E.164 format.
 *
 * @param input - The phone number as a string, potentially including non-numeric characters.
 * @returns The normalized phone number string or an empty string if input is not a string.
 */
export const normalizePhoneNumberToE164 = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) {
    return ''
  }
  const parsed = parseAndInferRegion(input)
  if (!parsed) {
    return input
  }
  return phoneUtil.format(parsed.number, PhoneNumberFormat.E164)
}

export const PHONE_NUMBER_MASK = [
  '(###) ###-####',
  '+# (###) ###-####',
  '+## ## #### ####',
]

export const INVALID_PHONE_NUMBER_MESSAGE =
  'Phone number must be valid. Include international code if applicable'
export const PHONE_NUMBER_ALREADY_EXISTS_MESSAGE = 'Phone number already in use'
